import {TaggedError} from 'effect/Data';
import {createLogger} from '~/shared/logging';
import {TurnstileAPI} from './TurnstileAPI.types';

const log = createLogger('TurnstileAPI');

const window: {
    turnstile: TurnstileAPI;
} & {
    [key in typeof turnstileLoadFn]?: () => void;
} = globalThis as any;

enum LoadState {
    Unloaded = 'unloaded',
    Loading = 'loading',
    Ready = 'ready',
}

let turnstileState = typeof window.turnstile !== 'undefined' ? LoadState.Ready : LoadState.Unloaded;
const turnstileLoad = Promise.withResolvers<TurnstileAPI>();
const turnstileLoadFn = '__turnstileOnLoad';
const turnstileSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export function getTurnstileAPI() {
    if (turnstileState === LoadState.Unloaded) {
        turnstileState = LoadState.Loading;
        window[turnstileLoadFn] = function onTurnstileLoad() {
            log.info('Script load done');
            delete window[turnstileLoadFn];
            turnstileLoad.resolve(window.turnstile);
            turnstileState = LoadState.Ready;
        };
        const url = `${turnstileSrc}?onload=${turnstileLoadFn}&render=explicit`;
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.addEventListener('error', () => {
            log.fail('Script load failure');
            delete window[turnstileLoadFn];
            turnstileLoad.reject(new Error('Failed to load Turnstile.'));
        });
        document.head.appendChild(script);
    }
    return turnstileLoad.promise;
}

export async function getTurnstileToken(sitekey: string, signal: AbortSignal) {
    const api = await getTurnstileAPI();
    const out = Promise.withResolvers<string>();
    const div = createInvisibleDiv();
    out.promise.finally(() => {
        api.remove(div);
        div.remove();
    });
    signal.addEventListener('abort', () => {
        out.reject(signal.reason);
    });
    api.render(div, {
        sitekey,
        'execution': 'render',
        'size': 'invisible',
        'callback': token => {
            out.resolve(token);
        },
        'error-callback': message => {
            out.reject(new TurnstileError({message}));
        },
        'expired-callback': token => {
            log.warn(`Token expired: ${token}.`);
        },
        'timeout-callback': () => {
            log.warn('Challenge expired.');
        },
        // these are hopefully will never run
        'unsupported-callback': () => {
            log.fail('Browser unsupported.');
        },
        'after-interactive-callback': () => {
            log.warn('Left interactive mode.');
        },
        'before-interactive-callback': () => {
            log.warn('Enters interactive mode.');
        },
    });

    return out.promise;
}

export class TurnstileError extends TaggedError('TurnstileError')<{message: string}> {
    readonly name = 'TurnstileError';
    readonly message: string;
    constructor(args: {message: string}) {
        super(args);
        this.message = args.message;
    }
}

function createInvisibleDiv() {
    const div = document.body.appendChild(document.createElement('div'));
    // sr-only
    div.style.position = 'absolute';
    div.style.width = '1px';
    div.style.height = '1px';
    div.style.padding = '0';
    div.style.margin = '-1px';
    div.style.overflow = 'hidden';
    div.style.clipPath = 'rect(0,0,0,0)';
    div.style.whiteSpace = 'nowrap';
    div.style.borderWidth = '0';
    return div;
}
